/**
 * Billing Activity Dependencies Configuration
 *
 * Allows configuring dependency rules for billing activities:
 * - Mutual exclusion rules (activities that cannot be billed together)
 * - Prerequisite rules (activities required before others)
 * - Episode limits (max units per pregnancy/episode)
 * - Violations log
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Plus,
  Trash2,
  AlertTriangle,
  Ban,
  ArrowRight,
  Clock,
  FileText,
  Upload,
  Download,
  CheckCircle,
  XCircle,
  AlertCircle,
  Edit2,
} from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import billingDependenciesAPI, {
  BillingDependency,
  DependencyType,
  ScopeType,
  ActivityGroup,
  EpisodeLimit,
  ViolationLog,
  BillingDependencyCreate,
  ActivityGroupCreate,
  ActivityGroupUpdate,
  EpisodeLimitCreate,
  GroupType,
} from '@/services/api/billingDependencies';
import { BillingActivity } from '@/services/api/billingActivities';

interface BillingActivityDependenciesProps {
  programId: number;
  programName?: string;
  activities: BillingActivity[];
  onUpdate?: () => void;
}

const dependencyTypeLabels: Record<DependencyType, string> = {
  mutual_exclusion: 'Mutual Exclusion',
  prerequisite: 'Prerequisite',
  conditional_prerequisite: 'Conditional Prerequisite',
  bundled_required: 'Bundled Required',
};

const scopeTypeLabels: Record<ScopeType, string> = {
  member: 'Per Member (Lifetime)',
  episode: 'Per Episode',
  enrollment: 'Per Enrollment',
  program: 'Per Program',
};

export function BillingActivityDependencies({
  programId,
  programName,
  activities,
  onUpdate,
}: BillingActivityDependenciesProps) {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const [activeTab, setActiveTab] = useState('dependencies');
  const [dependencies, setDependencies] = useState<BillingDependency[]>([]);
  const [groups, setGroups] = useState<ActivityGroup[]>([]);
  const [episodeLimits, setEpisodeLimits] = useState<EpisodeLimit[]>([]);
  const [violations, setViolations] = useState<ViolationLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form states
  const [showDependencyForm, setShowDependencyForm] = useState(false);
  const [showGroupForm, setShowGroupForm] = useState(false);
  const [showLimitForm, setShowLimitForm] = useState(false);

  const [dependencyForm, setDependencyForm] = useState<BillingDependencyCreate>({
    activity_id: 0,
    target_activity_id: 0,
    dependency_type: 'mutual_exclusion',
    scope_type: 'episode',
    description: '',
  });

  const [groupForm, setGroupForm] = useState<ActivityGroupCreate>({
    program_id: programId,
    group_name: '',
    group_type: 'mutual_exclusion',
    description: '',
    activity_ids: [],
  });

  // Edit group state
  const [editingGroup, setEditingGroup] = useState<ActivityGroup | null>(null);
  const [showEditGroupForm, setShowEditGroupForm] = useState(false);
  const [editGroupForm, setEditGroupForm] = useState<ActivityGroupUpdate>({
    group_name: '',
    group_type: 'mutual_exclusion',
    description: '',
    activity_ids: [],
  });

  const [limitForm, setLimitForm] = useState<EpisodeLimitCreate>({
    activity_id: 0,
    episode_type: 'pregnancy',
    max_units_per_episode: 1,
    description: '',
  });

  // Fetch data
  const fetchDependencies = useCallback(async () => {
    try {
      const data = await billingDependenciesAPI.getDependenciesForProgram(programId);
      setDependencies(data);
    } catch (err: any) {
      console.error('Failed to fetch dependencies:', err);
    }
  }, [programId]);

  const fetchGroups = useCallback(async () => {
    try {
      const data = await billingDependenciesAPI.getGroupsForProgram(programId);
      setGroups(data);
    } catch (err: any) {
      console.error('Failed to fetch groups:', err);
    }
  }, [programId]);

  const fetchViolations = useCallback(async () => {
    try {
      const data = await billingDependenciesAPI.getViolationLog({ limit: 50 });
      setViolations(data);
    } catch (err: any) {
      console.error('Failed to fetch violations:', err);
    }
  }, []);

  useEffect(() => {
    fetchDependencies();
    fetchGroups();
    fetchViolations();
  }, [fetchDependencies, fetchGroups, fetchViolations]);

  // Handlers
  const handleCreateDependency = async () => {
    if (!dependencyForm.activity_id || !dependencyForm.target_activity_id) {
      setError('Please select both activities');
      return;
    }
    if (dependencyForm.activity_id === dependencyForm.target_activity_id) {
      setError('Cannot create a dependency between the same activity');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await billingDependenciesAPI.createDependency(dependencyForm);
      setShowDependencyForm(false);
      setDependencyForm({
        activity_id: 0,
        target_activity_id: 0,
        dependency_type: 'mutual_exclusion',
        scope_type: 'episode',
        description: '',
      });
      await fetchDependencies();
      onUpdate?.();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to create dependency');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDependency = async (dependencyId: number) => {
    if (!window.confirm('Are you sure you want to delete this dependency rule?')) {
      return;
    }

    try {
      await billingDependenciesAPI.deleteDependency(dependencyId);
      await fetchDependencies();
      onUpdate?.();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete dependency');
    }
  };

  const handleCreateGroup = async () => {
    if (!groupForm.group_name || groupForm.activity_ids.length < 2) {
      setError('Please provide a group name and select at least 2 activities');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await billingDependenciesAPI.createActivityGroup({
        ...groupForm,
        program_id: programId,
      });
      setShowGroupForm(false);
      setGroupForm({
        program_id: programId,
        group_name: '',
        group_type: 'mutual_exclusion',
        description: '',
        activity_ids: [],
      });
      await fetchGroups();
      onUpdate?.();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to create group');
    } finally {
      setLoading(false);
    }
  };

  const handleEditGroup = (group: ActivityGroup) => {
    setEditingGroup(group);
    setEditGroupForm({
      group_name: group.group_name,
      group_type: group.group_type as GroupType,
      description: group.description || '',
      activity_ids: group.activities.map(a => a.id),
    });
    setShowEditGroupForm(true);
  };

  const handleUpdateGroup = async () => {
    if (!editingGroup) return;

    if (!editGroupForm.group_name || (editGroupForm.activity_ids && editGroupForm.activity_ids.length < 2)) {
      setError('Please provide a group name and select at least 2 activities');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await billingDependenciesAPI.updateActivityGroup(editingGroup.id, editGroupForm);
      setShowEditGroupForm(false);
      setEditingGroup(null);
      setEditGroupForm({
        group_name: '',
        group_type: 'mutual_exclusion',
        description: '',
        activity_ids: [],
      });
      await fetchGroups();
      onUpdate?.();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update group');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteGroup = async (groupId: number) => {
    if (!window.confirm('Are you sure you want to delete this group?')) {
      return;
    }

    try {
      await billingDependenciesAPI.deleteActivityGroup(groupId);
      await fetchGroups();
      onUpdate?.();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete group');
    }
  };

  const toggleActivityInEditGroup = (activityId: number) => {
    setEditGroupForm((prev) => ({
      ...prev,
      activity_ids: prev.activity_ids?.includes(activityId)
        ? prev.activity_ids.filter((id) => id !== activityId)
        : [...(prev.activity_ids || []), activityId],
    }));
  };

  const handleCreateLimit = async () => {
    if (!limitForm.activity_id || limitForm.max_units_per_episode < 1) {
      setError('Please select an activity and set a valid limit');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await billingDependenciesAPI.createEpisodeLimit(limitForm);
      setShowLimitForm(false);
      setLimitForm({
        activity_id: 0,
        episode_type: 'pregnancy',
        max_units_per_episode: 1,
        description: '',
      });
      // Refetch limits for the current activity if needed
      onUpdate?.();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to create episode limit');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      const blob = await billingDependenciesAPI.exportDependenciesCSV(programId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `dependency_rules_${programId}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError('Failed to export CSV');
    }
  };

  const handleImportCSV = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setLoading(true);
    setError(null);

    try {
      const result = await billingDependenciesAPI.importDependenciesCSV(programId, file);
      if (result.errors.length > 0) {
        setError(`Imported ${result.imported} rules. Errors: ${result.errors.join(', ')}`);
      }
      await fetchDependencies();
      onUpdate?.();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to import CSV');
    } finally {
      setLoading(false);
      event.target.value = '';
    }
  };

  const handleResolveViolation = async (
    violationId: number,
    status: 'overridden' | 'corrected' | 'dismissed'
  ) => {
    try {
      await billingDependenciesAPI.resolveViolation(violationId, status);
      await fetchViolations();
    } catch (err: any) {
      setError('Failed to resolve violation');
    }
  };

  const toggleActivityInGroup = (activityId: number) => {
    setGroupForm((prev) => ({
      ...prev,
      activity_ids: prev.activity_ids.includes(activityId)
        ? prev.activity_ids.filter((id) => id !== activityId)
        : [...prev.activity_ids, activityId],
    }));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <AlertTriangle className="h-6 w-6" />
            {t('billing.dependency_rules', 'Dependency Rules')}
          </h2>
          {programName && (
            <p className="text-sm text-muted-foreground mt-1">
              Configure activity dependencies for {programName}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleExportCSV}>
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          <label>
            <Button variant="outline" size="sm" asChild>
              <span>
                <Upload className="h-4 w-4 mr-2" />
                Import CSV
              </span>
            </Button>
            <input
              type="file"
              accept=".csv"
              className="hidden"
              onChange={handleImportCSV}
            />
          </label>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="dependencies">
            <ArrowRight className="h-4 w-4 mr-2" />
            Dependencies
          </TabsTrigger>
          <TabsTrigger value="groups">
            <Ban className="h-4 w-4 mr-2" />
            Exclusion Groups
          </TabsTrigger>
          <TabsTrigger value="limits">
            <Clock className="h-4 w-4 mr-2" />
            Episode Limits
          </TabsTrigger>
          <TabsTrigger value="violations">
            <FileText className="h-4 w-4 mr-2" />
            Violations Log
          </TabsTrigger>
        </TabsList>

        {/* Dependencies Tab */}
        <TabsContent value="dependencies" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Activity Dependencies</CardTitle>
              <Dialog open={showDependencyForm} onOpenChange={setShowDependencyForm}>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Rule
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add Dependency Rule</DialogTitle>
                    <DialogDescription>
                      Define a relationship between two billing activities
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div>
                      <Label>Source Activity</Label>
                      <Select
                        value={dependencyForm.activity_id ? String(dependencyForm.activity_id) : undefined}
                        onValueChange={(v) =>
                          setDependencyForm({ ...dependencyForm, activity_id: Number(v) })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select activity..." />
                        </SelectTrigger>
                        <SelectContent>
                          {activities.map((a) => (
                            <SelectItem key={a.id} value={String(a.id)}>
                              {a.activity_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Dependency Type</Label>
                      <Select
                        value={dependencyForm.dependency_type}
                        onValueChange={(v) =>
                          setDependencyForm({
                            ...dependencyForm,
                            dependency_type: v as DependencyType,
                          })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="mutual_exclusion">
                            Mutual Exclusion (cannot bill both)
                          </SelectItem>
                          <SelectItem value="prerequisite">
                            Prerequisite (must complete first)
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Target Activity</Label>
                      <Select
                        value={dependencyForm.target_activity_id ? String(dependencyForm.target_activity_id) : undefined}
                        onValueChange={(v) =>
                          setDependencyForm({ ...dependencyForm, target_activity_id: Number(v) })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select activity..." />
                        </SelectTrigger>
                        <SelectContent>
                          {activities
                            .filter((a) => a.id !== dependencyForm.activity_id)
                            .map((a) => (
                              <SelectItem key={a.id} value={String(a.id)}>
                                {a.activity_name}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Scope</Label>
                      <Select
                        value={dependencyForm.scope_type}
                        onValueChange={(v) =>
                          setDependencyForm({ ...dependencyForm, scope_type: v as ScopeType })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="episode">Per Episode (e.g., pregnancy)</SelectItem>
                          <SelectItem value="member">Per Member (lifetime)</SelectItem>
                          <SelectItem value="enrollment">Per Program Enrollment</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Description (optional)</Label>
                      <Input
                        value={dependencyForm.description || ''}
                        onChange={(e) =>
                          setDependencyForm({ ...dependencyForm, description: e.target.value })
                        }
                        placeholder="Describe this rule..."
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowDependencyForm(false)}>
                      Cancel
                    </Button>
                    <Button onClick={handleCreateDependency} disabled={loading}>
                      {loading ? 'Creating...' : 'Create Rule'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {dependencies.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  No dependency rules defined. Add rules to enforce billing constraints.
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Source Activity</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Target Activity</TableHead>
                      <TableHead>Scope</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-[100px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dependencies.map((dep) => (
                      <TableRow key={dep.id}>
                        <TableCell className="font-medium">{dep.activity_name}</TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              dep.dependency_type === 'mutual_exclusion' ? 'destructive' : 'default'
                            }
                          >
                            {dependencyTypeLabels[dep.dependency_type as DependencyType]}
                          </Badge>
                        </TableCell>
                        <TableCell>{dep.target_activity_name}</TableCell>
                        <TableCell>{scopeTypeLabels[dep.scope_type as ScopeType]}</TableCell>
                        <TableCell>
                          {dep.is_active ? (
                            <Badge variant="outline" className="text-green-600">
                              Active
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-gray-500">
                              Inactive
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteDependency(dep.id)}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Groups Tab */}
        <TabsContent value="groups" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Mutual Exclusion Groups</CardTitle>
              <Dialog open={showGroupForm} onOpenChange={setShowGroupForm}>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Group
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Create Exclusion Group</DialogTitle>
                    <DialogDescription>
                      Only one activity from this group can be billed per episode
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div>
                      <Label>Group Name</Label>
                      <Input
                        value={groupForm.group_name}
                        onChange={(e) =>
                          setGroupForm({ ...groupForm, group_name: e.target.value })
                        }
                        placeholder="e.g., Extended Initial Visits"
                      />
                    </div>
                    <div>
                      <Label>Description (optional)</Label>
                      <Input
                        value={groupForm.description || ''}
                        onChange={(e) =>
                          setGroupForm({ ...groupForm, description: e.target.value })
                        }
                        placeholder="Describe this group..."
                      />
                    </div>
                    <div>
                      <Label>Select Activities (min 2)</Label>
                      <div className="border rounded-md p-3 max-h-48 overflow-y-auto space-y-2 mt-2">
                        {activities.map((a) => (
                          <label
                            key={a.id}
                            className="flex items-center gap-2 cursor-pointer hover:bg-muted p-1 rounded"
                          >
                            <input
                              type="checkbox"
                              checked={groupForm.activity_ids.includes(a.id)}
                              onChange={() => toggleActivityInGroup(a.id)}
                              className="h-4 w-4"
                            />
                            <span>{a.activity_name}</span>
                          </label>
                        ))}
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        Selected: {groupForm.activity_ids.length} activities
                      </p>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowGroupForm(false)}>
                      Cancel
                    </Button>
                    <Button onClick={handleCreateGroup} disabled={loading}>
                      {loading ? 'Creating...' : 'Create Group'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {groups.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  No exclusion groups defined. Create groups to enforce mutual exclusion rules.
                </p>
              ) : (
                <div className="space-y-4">
                  {groups.map((group) => (
                    <Card key={group.id}>
                      <CardContent className="pt-4">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h4 className="font-semibold">{group.group_name}</h4>
                            {group.description && (
                              <p className="text-sm text-muted-foreground">{group.description}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge>{group.group_type}</Badge>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditGroup(group)}
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteGroup(group.id)}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-3">
                          {group.activities.length > 0 ? (
                            group.activities.map((a) => (
                              <Badge key={a.id} variant="outline">
                                {a.activity_name}
                              </Badge>
                            ))
                          ) : (
                            <span className="text-sm text-muted-foreground italic">
                              No activities assigned to this group
                            </span>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Edit Group Dialog */}
          <Dialog open={showEditGroupForm} onOpenChange={setShowEditGroupForm}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Edit Group</DialogTitle>
                <DialogDescription>
                  Update the group settings and member activities
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label>Group Name</Label>
                  <Input
                    value={editGroupForm.group_name || ''}
                    onChange={(e) =>
                      setEditGroupForm({ ...editGroupForm, group_name: e.target.value })
                    }
                    placeholder="e.g., Extended Initial Visits"
                  />
                </div>
                <div>
                  <Label>Description (optional)</Label>
                  <Input
                    value={editGroupForm.description || ''}
                    onChange={(e) =>
                      setEditGroupForm({ ...editGroupForm, description: e.target.value })
                    }
                    placeholder="Describe this group..."
                  />
                </div>
                <div>
                  <Label>Select Activities (min 2)</Label>
                  <div className="border rounded-md p-3 max-h-48 overflow-y-auto space-y-2 mt-2">
                    {activities.map((a) => (
                      <label
                        key={a.id}
                        className="flex items-center gap-2 cursor-pointer hover:bg-muted p-1 rounded"
                      >
                        <input
                          type="checkbox"
                          checked={editGroupForm.activity_ids?.includes(a.id) || false}
                          onChange={() => toggleActivityInEditGroup(a.id)}
                          className="h-4 w-4"
                        />
                        <span>{a.activity_name}</span>
                      </label>
                    ))}
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    Selected: {editGroupForm.activity_ids?.length || 0} activities
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowEditGroupForm(false)}>
                  Cancel
                </Button>
                <Button onClick={handleUpdateGroup} disabled={loading}>
                  {loading ? 'Updating...' : 'Update Group'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Episode Limits Tab */}
        <TabsContent value="limits" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Episode Limits</CardTitle>
              <Dialog open={showLimitForm} onOpenChange={setShowLimitForm}>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Limit
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add Episode Limit</DialogTitle>
                    <DialogDescription>
                      Set maximum units allowed per episode (e.g., per pregnancy)
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div>
                      <Label>Activity</Label>
                      <Select
                        value={limitForm.activity_id ? String(limitForm.activity_id) : undefined}
                        onValueChange={(v) =>
                          setLimitForm({ ...limitForm, activity_id: Number(v) })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select activity..." />
                        </SelectTrigger>
                        <SelectContent>
                          {activities.map((a) => (
                            <SelectItem key={a.id} value={String(a.id)}>
                              {a.activity_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Episode Type</Label>
                      <Select
                        value={limitForm.episode_type}
                        onValueChange={(v) => setLimitForm({ ...limitForm, episode_type: v })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pregnancy">Pregnancy</SelectItem>
                          <SelectItem value="hospitalization">Hospitalization</SelectItem>
                          <SelectItem value="treatment_episode">Treatment Episode</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Maximum Units per Episode</Label>
                      <Input
                        type="number"
                        min={1}
                        value={limitForm.max_units_per_episode}
                        onChange={(e) =>
                          setLimitForm({
                            ...limitForm,
                            max_units_per_episode: Number(e.target.value),
                          })
                        }
                      />
                    </div>
                    <div>
                      <Label>Description (optional)</Label>
                      <Input
                        value={limitForm.description || ''}
                        onChange={(e) => setLimitForm({ ...limitForm, description: e.target.value })}
                        placeholder="e.g., Only 1 extended visit per pregnancy"
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowLimitForm(false)}>
                      Cancel
                    </Button>
                    <Button onClick={handleCreateLimit} disabled={loading}>
                      {loading ? 'Creating...' : 'Create Limit'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground text-center py-8">
                Episode limits can be configured per activity. Use the Add Limit button to set
                maximum units per pregnancy or episode.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Violations Log Tab */}
        <TabsContent value="violations" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Violations Log</CardTitle>
            </CardHeader>
            <CardContent>
              {violations.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  No violations logged. Violations will appear here when billing rules are
                  triggered.
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Message</TableHead>
                      <TableHead>Severity</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {violations.map((v) => (
                      <TableRow key={v.id}>
                        <TableCell>
                          {fmt.short(v.detected_at)}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{v.violation_type}</Badge>
                        </TableCell>
                        <TableCell className="max-w-xs truncate">{v.violation_message}</TableCell>
                        <TableCell>
                          <Badge
                            variant={v.severity === 'error' ? 'destructive' : 'secondary'}
                          >
                            {v.severity}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {v.resolution_status === 'unresolved' ? (
                            <Badge variant="outline" className="text-yellow-600">
                              Unresolved
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-green-600">
                              {v.resolution_status}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {v.resolution_status === 'unresolved' && (
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleResolveViolation(v.id, 'dismissed')}
                                title="Dismiss"
                              >
                                <XCircle className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleResolveViolation(v.id, 'overridden')}
                                title="Override"
                              >
                                <CheckCircle className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default BillingActivityDependencies;
